import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, RotateCcw, FileText, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/PageHeader";
import { EmployeeSheet } from "@/components/EmployeeSheet";
import { api, formatApiError, formatDate } from "@/lib/api";

const Days = ({ d }) => (
  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${d < 0 ? "bg-rose-50 text-rose-700 ring-rose-200" : d <= 7 ? "bg-amber-50 text-amber-800 ring-amber-200" : "bg-slate-50 text-slate-700 ring-slate-200"}`}>
    {d < 0 ? `scaduto da ${-d} g` : d === 0 ? "scade oggi" : `tra ${d} giorni`}
  </span>
);

export default function Deadlines() {
  const [data, setData] = useState({ rows: [], horizon_days: 30 });
  const [days, setDays] = useState("");
  const [sede, setSede] = useState("tutte");
  const [sedi, setSedi] = useState([]);
  const [onlyOpen, setOnlyOpen] = useState(true);
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    api.get("/contracts/deadlines", { params: { days: Number(days) || 0, sede: sede === "tutte" ? "" : sede, include_acked: !onlyOpen } })
      .then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [days, sede, onlyOpen]);
  useEffect(load, [load]);
  useEffect(() => { api.get("/settings").then((r) => setSedi(r.data.sedi || [])).catch(() => {}); }, []);

  const ack = async (r, on) => {
    try {
      if (on) await api.post(`/contracts/deadlines/${r.contract_id}/ack`); else await api.delete(`/contracts/deadlines/${r.contract_id}/ack`);
      toast.success(on ? "Avviso confermato" : "Conferma annullata"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const rows = data.rows.filter((r) => !q || r.employee_name.toLowerCase().includes(q.toLowerCase()));

  return (
    <>
      <PageHeader eyebrow="Controllo" title="Scadenze" description={`Contratti a termine che scadono entro ${data.horizon_days} giorni (o già scaduti). I contratti a tempo indeterminato non hanno scadenza.`} />
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca dipendente" className="sm:max-w-xs" data-testid="deadlines-search-input" />
        <Input type="number" min={1} value={days} onChange={(e) => setDays(e.target.value)} placeholder={`Giorni (${data.horizon_days})`} className="sm:w-36" data-testid="deadlines-days-input" />
        <Select value={sede} onValueChange={setSede}>
          <SelectTrigger className="sm:w-48" data-testid="deadlines-sede-select"><Filter className="mr-2 h-3.5 w-3.5" /><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="tutte">Tutte le sedi</SelectItem>{sedi.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant={onlyOpen ? "default" : "outline"} onClick={() => setOnlyOpen((v) => !v)} data-testid="deadlines-only-open-toggle">{onlyOpen ? "Solo da confermare" : "Tutte"}</Button>
      </div>
      <div className="panel overflow-x-auto" data-testid="deadlines-table">
        <Table className="min-w-[800px]">
          <TableHeader><TableRow><TableHead>Dipendente</TableHead><TableHead>Sede</TableHead><TableHead>Contratto</TableHead><TableHead>Inizio</TableHead><TableHead>Scadenza</TableHead><TableHead>Avviso</TableHead><TableHead className="text-right">Azioni</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.contract_id} data-testid={`deadline-row-${r.contract_id}`} className={r.acked ? "opacity-60" : ""}>
                <TableCell><button className="font-medium text-slate-900 hover:underline" onClick={() => setOpenId(r.employee_id)} data-testid={`deadline-open-${r.contract_id}`}>{r.employee_name}</button><p className="text-xs text-slate-500">{r.mansione}</p></TableCell>
                <TableCell className="text-sm">{r.sede || "—"}</TableCell>
                <TableCell className="text-sm">{r.tipologia_contratto || "—"} <span className="text-xs text-slate-400">({r.tipo_documento})</span></TableCell>
                <TableCell className="text-sm">{r.data_inizio ? formatDate(r.data_inizio, false) : "—"}</TableCell>
                <TableCell className="text-sm font-medium">{formatDate(r.data_fine, false)} <Days d={r.days} /></TableCell>
                <TableCell className="text-xs text-slate-500">{r.acked ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> {r.acked_by} · {formatDate(r.acked_at)}</span> : "Da confermare"}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" asChild><a href={`${process.env.REACT_APP_BACKEND_URL}/api/contracts/${r.contract_id}/file`} target="_blank" rel="noreferrer" data-testid={`deadline-file-${r.contract_id}`}><FileText className="h-4 w-4" /></a></Button>
                    {r.acked ? <Button size="sm" variant="ghost" onClick={() => ack(r, false)} data-testid={`deadline-unack-${r.contract_id}`}><RotateCcw className="h-4 w-4" /></Button>
                      : <Button size="sm" onClick={() => ack(r, true)} data-testid={`deadline-ack-${r.contract_id}`}><CheckCircle2 className="mr-1 h-4 w-4" /> Conferma</Button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="py-10 text-center text-sm text-slate-500" data-testid="deadlines-empty">Nessuna scadenza in questo filtro.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <EmployeeSheet employeeId={openId} onClose={() => setOpenId(null)} />
    </>
  );
}
